/**
 * A collection of grid layout components for creating responsive layouts.
 * Uses a 12-column grid system with responsive breakpoints.
 * It should be used in environments where `bootstrap-grid` is not available (e.g. shop, shop-components).
 * @module Grid
 */

import { Container, Row, Col } from './styles'

/**
 * Available breakpoints for responsive design
 * @typedef {Object} Breakpoints
 * @property {string} xs - Extra small devices (< 576px)
 * @property {string} sm - Small devices (≥ 576px)
 * @property {string} md - Medium devices (≥ 768px)
 * @property {string} lg - Large devices (≥ 992px)
 * @property {string} xl - Extra large devices (≥ 1200px)
 */

/**
 * Column span configuration for different breakpoints
 * @typedef {Object} ColSpan
 * @property {number} [xs] - Number of columns to span on extra small devices
 * @property {number} [sm] - Number of columns to span on small devices
 * @property {number} [md] - Number of columns to span on medium devices
 * @property {number} [lg] - Number of columns to span on large devices
 * @property {number} [xl] - Number of columns to span on extra large devices
 */

/**
 * @typedef {Object} GridComponents
 * @property {typeof Container} Container - A container component that provides consistent padding and max-width
 * @property {typeof Row} Row - A row component that creates horizontal groups of columns
 * @property {typeof Col} Col - A column component that specifies content width within a row
 */

/**
 * Grid system components for creating responsive layouts.
 * @type {GridComponents}
 *
 * @example
 * import Grid from './Grid'
 *
 * function Layout() {
 *   return (
 *     <Grid.Container>
 *       <Grid.Row>
 *         <Grid.Col xs={12} md={6} lg={4}>Content</Grid.Col>
 *       </Grid.Row>
 *     </Grid.Container>
 *   )
 * }
 */
const Grid = {}

Grid.Container = Container
Grid.Row = Row
Grid.Col = Col

/**
 * A container component that provides consistent padding and max-width.
 * Container widths are responsive and determined by breakpoints.
 *
 * @component
 * @property {boolean} [fluid] - If true, the container will be full-width without max-width constraints
 *
 * Default max-widths per breakpoint:
 * - sm: 540px
 * - md: 720px
 * - lg: 960px
 * - xl: 1140px
 *
 * @example
 * // Fixed-width container
 * <Container>Content</Container>
 *
 * // Fluid container
 * <Container fluid>Content</Container>
 */
export { Container }

/**
 * A row component that creates a 12-column grid layout.
 * Uses CSS Grid under the hood with configurable gap spacing.
 *
 * @component
 * @property {number} [gap] - Custom gap size in pixels between columns
 * @property {boolean} [noGutter] - If true, removes all spacing between columns
 *
 * @example
 * // Default row with 15px gap
 * <Row>
 *   <Col>Content</Col>
 * </Row>
 *
 * // Row with custom gap
 * <Row gap={20}>
 *   <Col>Content</Col>
 * </Row>
 *
 * // Row with no gutters
 * <Row noGutter>
 *   <Col>Content</Col>
 * </Row>
 */
export { Row }

/**
 * A column component that specifies content width within a row.
 * Spans full width by default on mobile (xs breakpoint).
 * Column spans can be specified for each breakpoint.
 *
 * @component
 * @property {number} [xs] - Number of columns to span on extra small devices (<576px)
 * @property {number} [sm] - Number of columns to span on small devices (≥576px)
 * @property {number} [md] - Number of columns to span on medium devices (≥768px)
 * @property {number} [lg] - Number of columns to span on large devices (≥992px)
 * @property {number} [xl] - Number of columns to span on extra large devices (≥1200px)
 *
 * @example
 * // Responsive column that spans:
 * // - 12 columns on mobile (<576px)
 * // - 6 columns on medium devices (≥768px)
 * // - 4 columns on large devices (≥992px)
 * <Col xs={12} md={6} lg={4}>Content</Col>
 *
 * // Column that spans 6 columns on all breakpoints
 * <Col xs={6}>Content</Col>
 */
export { Col }

export default Grid
